/* The queue's right-hand detail panel — SAME architecture as the deck's
 * document panel: it describes the selected row and offers exactly two
 * moves, "Open in surface →" (into the kept pane) and Snooze. It never
 * decides anything itself.
 *
 * SENTENCES, NOT LABELS (Ale 2026-09-02): the header is the task's
 * sentence — fund, object, figures and age as data inside it — not the
 * engine title over a type · fund · age · priority strip. Priority stays
 * as its badge (a state, not a label). */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import { SURFACE_SLUGS, surfaceFor } from './placement'
import { priorityClass } from './queueUtils'
import Sentence, { SentenceParts } from '../Sentence'
import { taskSentence } from '../taskSentence'
import { engineReasonParts } from '../../lib/sentences/compile'
import { HelpDot } from '../guidance/Guidance'

/** The engine's reason for a task (its description) as a sentence with
 *  the record values it names — document names, dates, figures — as data
 *  (S6, 2026-09-03). A raw key=value dump (the recon diagnoses) is not
 *  prose: its values already sit in the header sentence, so it is not
 *  repeated. */
function reasonParts(description: string | null) {
  if (!description) return []
  const dumpy = (description.match(/\b[a-z_]+=\S+/g) ?? []).length >= 2
  return dumpy ? [] : engineReasonParts(description)
}

export default function TaskDetailPanel({
  task,
  onClose,
  onSnoozed,
}: {
  task: TaskQueueItem
  onClose: () => void
  onSnoozed?: () => void
}) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const surface = surfaceFor(task.task_type)
  const slug = surface ? SURFACE_SLUGS[surface] : 'other-ops'
  const s = taskSentence(task)
  const reason = reasonParts(task.description)

  async function snooze() {
    setBusy(true)
    setNote(null)
    try {
      await apiFetch(`/api/tasks/${task.task_id}/snooze`, {
        method: 'POST',
        body: JSON.stringify({ mode: 'next_document' }),
      })
      setNote('Parked — it wakes on the next document for its object.')
      onSnoozed?.()
    } catch (err) {
      // A non-snoozable type answers 409 with an honest reason; show it
      // rather than pretending the button did something.
      setNote(err instanceof Error ? err.message : 'Could not park this one.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <aside className="dpanel" data-testid="wb-detail-panel">
      <h2 title={task.title}>
        <Sentence
          meaning={s.meaning}
          values={s.values}
          fallback={s.fallback}
          data-testid="wb-detail-sentence"
        />
      </h2>
      <div className="dmeta">
        <span className={`pbadge ${priorityClass(task)}`}>{task.priority}</span>
        <HelpDot k="canon.datum" />
      </div>
      {reason.length ? (
        <p className="dsummary sn" data-testid="wb-detail-reason">
          <SentenceParts parts={reason} />
        </p>
      ) : null}
      <div className="dacts">
        <button
          type="button"
          className="btn primary"
          data-testid="wb-open-in-surface"
          onClick={() => navigate(`/v2/workbench/${slug}/task/${task.task_id}`)}
        >
          Open →
        </button>
        <button type="button" className="btn" disabled={busy} onClick={snooze}>
          Snooze
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </div>
      {note ? <p className="bulknote">{note}</p> : null}
    </aside>
  )
}
